import { useSelector } from "react-redux";
import { Redirect, Route, RouteComponentProps } from "react-router-dom";

import { useAuth } from "@contexts/auth/AuthProvider";
import { Case } from "@types-runway/case";
import { VALID_QUESTIONNAIRE_LENGTH } from "@types-runway/questionnaire";
import { RootState } from "@types-runway/state";

type PublicRouteProps = {
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
};

type ExtendedRouteComponentProps = RouteComponentProps<
  {
    [x: string]: string | undefined;
  },
  any,
  unknown
>;

const PublicRoute: React.FC<PublicRouteProps> = ({ component: Component, ...rest }) => {
  const { isAuth } = useAuth();
  const newCase = useSelector<RootState, Case>((state) => state.case.newCase.data);
  const hasCompletedQuestionnaire =
    newCase.case_questions?.length ?? 0 >= VALID_QUESTIONNAIRE_LENGTH;

  const RouteHandler = (props: ExtendedRouteComponentProps): React.ReactNode => {
    const { path } = props.match;

    const handleAuthenticationFlow = () => {
      const isAuthPage = path === "/login" || path === "/registration";
      const hasCaseId = newCase.id && newCase.id > 0;

      if (!isAuthPage || !isAuth) return null;

      // If consultation exists or user is signing up, redirect to checkout or consultation
      if (hasCaseId || path === "/registration") {
        if (hasCompletedQuestionnaire) {
          return <Redirect to="/checkout" />;
        }
        return <Redirect to="/consultation" />;
      }
      return null;
    };

    const handleAuthRedirect = () => {
      if (isAuth) {
        return <Redirect to="/dashboard" />;
      }
      return <Component {...props} />;
    };

    // Execution flow
    return handleAuthenticationFlow() || handleAuthRedirect();
  };

  return <Route {...rest} render={(props) => RouteHandler(props)} />;
};

export default PublicRoute;
